import { useCallback, useEffect, useState } from "react";
import { Copy, Pencil } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { request } from "../lib/api";
import { useAttribution } from "../lib/useAttribution";
import type { ContentItem, ContentType, Module, ModulesResponse, Notice } from "../types";
import { CONTENT_TYPE_LABELS } from "../types";
import { AttributionCell } from "../components/Attribution";
import { AudienceModal } from "../components/AudienceModal";
import { DragList } from "../components/DragList";
import { ContentTranslationEditor } from "./ContentTranslation";
import { ContentEditor } from "../components/ContentEditor";
import {
  Card,
  ConfirmModal,
  InlineNotice,
  errorMessage,
  inputClass,
  labelClass,
  secondaryButtonClass,
} from "../components/ui";

const requiresQuestionBuilder = (type: ContentType) => type === "quiz";
const requiresFormBuilder = (type: ContentType) => type === "form";

interface ItemsResponse {
  items: ContentItem[];
}

export function Content() {
  const [searchParams] = useSearchParams();
  const [modules, setModules] = useState<Module[]>([]);
  const [cohorts, setCohorts] = useState<{ id: string; name: string }[]>([]);
  const [moduleID, setModuleID] = useState(searchParams.get("module_id") || "");
  // The library is browsed the way the programme is built: cohort, then its PD
  // day, then that day's module, then the module's content.
  const [cohortChoice, setCohortChoice] = useState("");
  const [pdDayChoice, setPdDayChoice] = useState("");
  const [audienceFor, setAudienceFor] = useState<{ id: string; title: string } | null>(null);
  const [items, setItems] = useState<ContentItem[]>([]);
  const attribution = useAttribution(
    "content-items",
    items.map((item) => item.id)
  );
  const [editingID, setEditingID] = useState<string | null>(searchParams.get("item_id"));
  const [translatingID, setTranslatingID] = useState<string | null>(null);
  const openEditor = (id: string | null) => {
    setTranslatingID(null);
    setEditingID(id);
  };
  const openTranslation = (id: string) => {
    setEditingID(null);
    setTranslatingID(id);
  };
  const [message, setMessage] = useState<Notice | null>(null);
  const [confirmDeleteID, setConfirmDeleteID] = useState<string | null>(null);

  useEffect(() => {
    request<ModulesResponse>("/api/admin/modules")
      .then((data) => setModules(data.modules))
      .catch(() => setModules([]));
    request<{ items: { id: string; name: string }[] }>("/api/admin/structure/cohorts")
      .then((data) => setCohorts(data.items))
      .catch(() => setCohorts([]));
  }, []);

  // A link to one module (module_id in the address) opens with its cohort and
  // PD day already chosen.
  useEffect(() => {
    const current = modules.find((module) => module.id === moduleID);
    if (current) {
      setCohortChoice(current.cohort_id);
      setPdDayChoice(current.pd_day_id);
    }
  }, [modules, moduleID]);

  const cohortOptions = cohorts.filter((cohort) => modules.some((m) => m.cohort_id === cohort.id));
  const cohortID = cohortChoice || (cohortOptions.length === 1 ? cohortOptions[0].id : "");
  const pdDayOptions = [
    ...new Map(
      modules
        .filter((module) => module.cohort_id === cohortID)
        .map((module) => [module.pd_day_id, module.pd_day])
    ),
  ];
  const pdDayID = pdDayChoice || (pdDayOptions.length === 1 ? pdDayOptions[0][0] : "");
  const moduleOptions = modules.filter((module) => module.pd_day_id === pdDayID);

  const loadItems = useCallback(() => {
    if (!moduleID) return;
    request<ItemsResponse>(`/api/admin/content-items?module_id=${moduleID}`)
      .then((data) => setItems(data.items.sort((a, b) => a.position - b.position)))
      .catch(() => setItems([]));
  }, [moduleID]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  async function reorder(nextItems: ContentItem[]) {
    setItems(nextItems);
    try {
      await request("/api/admin/content-items/reorder", {
        method: "POST",
        body: JSON.stringify({ module_id: moduleID, item_ids: nextItems.map((i) => i.id) }),
      });
      loadItems();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
      loadItems();
    }
  }

  async function duplicate(id: string) {
    try {
      await request(`/api/admin/content-items/${id}/duplicate`, { method: "POST" });
      setMessage({ tone: "success", text: "Item duplicated." });
      loadItems();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  async function remove(id: string) {
    try {
      await request(`/api/admin/content-items/${id}`, { method: "DELETE" });
      setMessage({ tone: "success", text: "Item deleted." });
      setConfirmDeleteID(null);
      loadItems();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  return (
    <div className="grid gap-6 min-[1800px]:grid-cols-[380px_minmax(0,1fr)]">
      <Card
        title="Module content"
        text="Choose a PD day and one of its modules to manage the ordered content items."
      >
        <div className="grid gap-3">
          {cohortOptions.length > 1 && (
            <label className={labelClass}>
              Cohort
              <select
                className={inputClass}
                value={cohortID}
                onChange={(e) => {
                  setCohortChoice(e.target.value);
                  setPdDayChoice("");
                  setModuleID("");
                  setTranslatingID(null);
                }}
              >
                <option value="">Select cohort</option>
                {cohortOptions.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={labelClass}>
            PD day
            <select
              className={inputClass}
              value={pdDayID}
              disabled={!cohortID}
              onChange={(e) => {
                setPdDayChoice(e.target.value);
                setModuleID("");
                setTranslatingID(null);
              }}
            >
              <option value="">Select PD day</option>
              {pdDayOptions.map(([id, title]) => (
                <option key={id} value={id}>
                  {title}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Module
            <select
              className={inputClass}
              value={moduleID}
              disabled={!pdDayID}
              onChange={(e) => {
                setModuleID(e.target.value);
                setTranslatingID(null);
              }}
            >
              <option value="">Select module</option>
              {moduleOptions.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.title} ({module.item_count} {module.item_count === 1 ? "item" : "items"})
                </option>
              ))}
            </select>
          </label>
        </div>
        {moduleID && (
          <Link
            className="mt-2 inline-block text-sm font-semibold text-interactive hover:underline"
            to={`/modules/${moduleID}`}
          >
            Back to module
          </Link>
        )}
        {message && <InlineNotice tone={message.tone} text={message.text} />}
        {moduleID && (
          <div className="mt-5">
            {items.length === 0 ? (
              <div className="grid gap-3">
                <p className="text-sm text-content-secondary">No content items yet.</p>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => openEditor("new")}
                >
                  + New content item
                </button>
              </div>
            ) : (
              <DragList
                items={items}
                onChange={reorder}
                onRemove={(i) => setConfirmDeleteID(items[i].id)}
                addLabel="New content item"
                onAdd={() => openEditor("new")}
                renderItem={(item) => (
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1">
                    <div className="min-w-0 flex-1 basis-32">
                      <button
                        type="button"
                        className="block max-w-full truncate text-start text-sm font-semibold text-content-primary hover:text-interactive"
                        onClick={() => openEditor(item.id)}
                      >
                        {item.title}
                      </button>
                      <p className="truncate text-xs text-content-secondary">
                        {CONTENT_TYPE_LABELS[item.type]} · {item.status}
                        {item.required ? " · required" : ""}
                      </p>
                      <div className="mt-1">
                        <AttributionCell info={attribution[item.id]} />
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                      {requiresQuestionBuilder(item.type) && (
                        <Link
                          className="text-xs font-semibold text-interactive hover:underline"
                          to={`/content/${item.id}/questions`}
                        >
                          Questions
                        </Link>
                      )}
                      {requiresFormBuilder(item.type) && (
                        <Link
                          className="text-xs font-semibold text-interactive hover:underline"
                          to={`/content/${item.id}/fields`}
                        >
                          Fields
                        </Link>
                      )}
                      <button
                        type="button"
                        className="text-xs font-semibold text-interactive hover:underline"
                        onClick={() => setAudienceFor({ id: item.id, title: item.title })}
                        aria-label={`Who sees ${item.title}`}
                      >
                        Audience
                      </button>
                      <button
                        type="button"
                        className="text-xs font-semibold text-interactive hover:underline"
                        onClick={() => openTranslation(item.id)}
                      >
                        اردو
                      </button>
                      <button
                        type="button"
                        className="text-content-secondary hover:text-interactive"
                        onClick={() => openEditor(item.id)}
                        aria-label="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        className="text-content-secondary hover:text-interactive"
                        onClick={() => duplicate(item.id)}
                        aria-label="Duplicate"
                      >
                        <Copy size={15} />
                      </button>
                    </div>
                  </div>
                )}
              />
            )}
          </div>
        )}
      </Card>
      {moduleID && translatingID ? (
        <ContentTranslationEditor
          key={translatingID}
          id={translatingID}
          onClose={() => setTranslatingID(null)}
        />
      ) : moduleID && editingID ? (
        <ContentEditor
          moduleID={moduleID}
          itemID={editingID === "new" ? null : editingID}
          onSaved={() => {
            setEditingID(null);
            loadItems();
          }}
          onCancel={() => setEditingID(null)}
          onRequestDelete={(id) => setConfirmDeleteID(id)}
        />
      ) : (
        <Card title="No item selected" text="Pick a content item to edit, or create a new one.">
          <p className="text-sm text-content-secondary">
            {moduleID
              ? "Click an item in the list to edit it, or use “+ New content item” to get started."
              : "Choose a PD day, then a module, on the left first."}
          </p>
        </Card>
      )}
      {audienceFor && (
        <AudienceModal
          resourceType="content_item"
          resourceID={audienceFor.id}
          title={audienceFor.title}
          onClose={() => setAudienceFor(null)}
        />
      )}
      <ConfirmModal
        open={Boolean(confirmDeleteID)}
        title="Delete content item?"
        message="This removes the item and its content permanently."
        confirmLabel="Delete"
        onConfirm={async () => {
          if (!confirmDeleteID) return;
          await remove(confirmDeleteID);
          setEditingID(null);
          setTranslatingID(null);
        }}
        onCancel={() => setConfirmDeleteID(null)}
      />
    </div>
  );
}
